import type { z } from 'zod'

/**
 * Zod スキーマから推論されるフィールドごとのエラー型
 */
export type FormErrors<T extends z.ZodTypeAny> = Partial<Record<keyof z.infer<T>, string>>

/**
 * Zod スキーマでバリデーションし、フィールドごとのエラーを返す
 */
export function validateForm<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
): { success: boolean; errors: FormErrors<T> } {
  const result = schema.safeParse(data)
  if (result.success) {
    return { success: true, errors: {} }
  }
  const errors: Record<string, string> = {}
  for (const issue of result.error.issues) {
    const key = String(issue.path[0])
    if (!errors[key]) {
      errors[key] = issue.message
    }
  }
  return { success: false, errors: errors as FormErrors<T> }
}

/**
 * touched されたフィールドのみエラーを返す
 */
export function getVisibleErrors(
  allErrors: Record<string, string>,
  touched: Record<string, boolean>,
): Record<string, string> {
  const visibleErrors: Record<string, string> = {}
  for (const [field, message] of Object.entries(allErrors)) {
    if (touched[field]) {
      visibleErrors[field] = message
    }
  }
  return visibleErrors
}

/**
 * Zod スキーマのフィールド + 追加フィールドを touched 状態にするオブジェクトを返す
 */
export function touchAll<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
  ...extraFields: string[]
): Record<string, boolean> {
  const touched: Record<string, boolean> = {}
  for (const key of Object.keys(schema.shape)) {
    touched[key] = true
  }
  for (const key of extraFields) {
    touched[key] = true
  }
  return touched
}
